import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import earthImg from "@/assets/images/manage-client-earth.png";
import { Typography } from "@/components/atoms";

export type ManageClientLoaderVariant = "accessing" | "returning";

interface ManageClientLoaderProps {
  variant: ManageClientLoaderVariant;
  /** Managed client's display name, woven into the subtitle copy. */
  clientName: string;
}

const COPY: Record<ManageClientLoaderVariant, { title: string; subtitle: (name: string) => string }> = {
  accessing: {
    title: "Accessing Client Account",
    subtitle: (name) => `Hang tight! Redirecting you to ${name}'s account so you can manage it seamlessly.`,
  },
  returning: {
    title: "Returning to Your Account",
    subtitle: (name) => `Wrapping up your session with ${name}'s account. Redirecting you now.`,
  },
};

/**
 * Full-screen dark takeover loader (DEV-409, Figma 5379-38323). Shown on entry ("accessing")
 * while the access link resolves, and on "Stop Managing" ("returning") before navigating back to
 * My Clients. Earth illustration (Figma asset `manage-client-earth.png`, node 5379-38310) + an
 * animated progress bar + title/subtitle. Rendered through a portal to `document.body` so the
 * `fixed inset-0` overlay escapes any clipped ancestor (cf. LoadingSplash).
 */
export const ManageClientLoader = ({ variant, clientName }: ManageClientLoaderProps) => {
  const { title, subtitle } = COPY[variant];
  const name = clientName || "the client";

  // Drive an indeterminate-feeling progress bar: ease toward ~90% so it reads as "almost there"
  // without ever implying completion (the real completion is the redirect that unmounts us).
  const [progress, setProgress] = useState(8);
  useEffect(() => {
    const id = window.setInterval(() => {
      setProgress((p) => (p >= 90 ? 90 : p + Math.max(1, Math.round((90 - p) / 6))));
    }, 220);
    return () => window.clearInterval(id);
  }, []);

  const content = (
    <div
      role="status"
      aria-live="polite"
      aria-label={title}
      className="fixed inset-0 z-[60] flex flex-col items-center justify-center bg-neutral-black px-6 text-center"
    >
      <img src={earthImg} alt="" aria-hidden className="h-40 w-40 select-none object-contain" />

      <Typography variant="h3" className="mt-6 text-white">
        {title}
      </Typography>
      <Typography variant="paragraph" className="mt-2 max-w-[360px] text-neutral-mid-gray">
        {subtitle(name)}
      </Typography>

      <div className="mt-8 h-1.5 w-[220px] overflow-hidden rounded-full bg-white/15">
        <div className="h-full rounded-full bg-primary transition-[width] duration-200 ease-out" style={{ width: `${progress}%` }} />
      </div>
    </div>
  );

  return typeof document === "undefined" ? content : createPortal(content, document.body);
};
